import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import { AlertTriangle, MapPin, Camera, ShieldOff, Hand, Lock, Check, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const FLAG_META = {
    missing_gps: { icon: MapPin, label: "no GPS", color: "text-destructive" },
    low_accuracy: { icon: AlertTriangle, label: "low accuracy", color: "text-yellow-600" },
    outside_geofence: { icon: ShieldOff, label: "outside geofence", color: "text-destructive" },
    remote_clockin: { icon: MapPin, label: "remote", color: "text-yellow-600" },
    manual_override: { icon: Hand, label: "manual override", color: "text-yellow-600" },
    rapid_repeat: { icon: AlertTriangle, label: "rapid repeat", color: "text-destructive" },
};

/* Drill-down: full record with GPS, flags, and history; approve/reject lives
 * here so a reviewer always verifies the detail before deciding. */
function AttendanceDetail({ rec, empName, onClose, onDecide }) {
    if (!rec) return null;
    const needsReview = rec.manual_override || rec.requires_holiday_approval || rec.approval_status === "pending";
    const canDecide = needsReview && !rec.payroll_locked && !rec.payroll_run_id
        && !["approved", "rejected"].includes(rec.review_status) && !["approved", "rejected"].includes(rec.approval_status || "");
    const Row = ({ label, children }) => (
        <div className="flex justify-between gap-3 py-1 border-b border-border/60 last:border-0 text-sm">
            <span className="text-muted-foreground text-xs pt-0.5">{label}</span>
            <span className="text-right">{children ?? "—"}</span>
        </div>
    );
    const gmap = (g) => g?.lat ? <a className="text-primary underline font-mono text-xs" href={`https://maps.google.com/?q=${g.lat},${g.lng}`} target="_blank" rel="noreferrer">{g.lat.toFixed(5)}, {g.lng.toFixed(5)}</a> : <span className="text-destructive text-xs">missing</span>;
    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Attendance — {rec.work_date} · {empName}</DialogTitle></DialogHeader>
                <div className="max-h-[65vh] overflow-auto pr-1 space-y-3">
                    {rec.is_holiday && (
                        <div className={`rounded-md p-2.5 text-sm border ${rec.approval_status === "pending" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`}>
                            🎌 <b>{rec.holiday_name || "Holiday"}</b> — {rec.holiday_type === "regular" ? "Regular Holiday (200% pay if worked)" : "Special Non-Working Day (+30% premium)"}
                            {rec.approval_status === "pending" && <span className="block text-xs mt-0.5">Holiday work needs pre-approval — approve below to credit the premium in payroll.</span>}
                        </div>
                    )}
                    <Surface className="p-3">
                        <Row label="Time in"><span className="font-mono text-xs">{rec.time_in ? formatDateTime(rec.time_in) : "—"}</span></Row>
                        <Row label="Time out"><span className="font-mono text-xs">{rec.time_out ? formatDateTime(rec.time_out) : "—"}</span></Row>
                        <Row label="Hours worked"><b>{(rec.hours_worked || 0).toFixed(2)}</b></Row>
                        {rec.late_minutes > 0 && <Row label="Late"><span className="badge badge-pending">{rec.late_minutes} min</span></Row>}
                        <Row label="Method"><StatusBadge value={rec.source} /></Row>
                        <Row label="GPS in">{gmap(rec.gps_in)}</Row>
                        {rec.gps_out && <Row label="GPS out">{gmap(rec.gps_out)}</Row>}
                        <Row label="GPS accuracy">{rec.accuracy_meters_in ? `±${Math.round(rec.accuracy_meters_in)} m` : "—"}</Row>
                        <Row label="Geofence">{rec.geofence_result ? <StatusBadge value={rec.geofence_result} /> : "—"}</Row>
                        <Row label="Selfie">{rec.selfie_in_path ? <span className="text-emerald-600 text-xs">✓ captured</span> : <span className="text-xs text-muted-foreground">none</span>}</Row>
                        {rec.manual_override && <Row label="Manual override">{rec.override_reason || rec.manual_reason || "yes"}</Row>}
                        <Row label="Review status">{rec.payroll_locked || rec.payroll_run_id ? <Badge variant="outline"><Lock className="size-3 mr-1" /> payroll-locked</Badge> : <StatusBadge value={rec.review_status || "auto"} />}</Row>
                        {rec.review_reason && <Row label="Review note">{rec.review_reason}</Row>}
                    </Surface>
                    {(rec.suspicious_flags || []).length > 0 && (
                        <Surface className="p-3">
                            <div className="text-xs font-semibold uppercase tracking-wide text-destructive mb-1.5 flex items-center gap-1"><AlertTriangle className="size-3" /> Suspicious flags — verify before approving</div>
                            <div className="flex flex-wrap gap-1">
                                {rec.suspicious_flags.map((f) => {
                                    const m = FLAG_META[f] || { icon: AlertTriangle, label: f, color: "text-muted-foreground" };
                                    const Icon = m.icon;
                                    return <span key={f} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded ${m.color} bg-secondary`}><Icon className="size-3" /> {m.label}</span>;
                                })}
                            </div>
                        </Surface>
                    )}
                </div>
                <DialogFooter>
                    {canDecide ? (
                        <div className="flex gap-2">
                            <button onClick={() => onDecide(rec.id, "rejected")} className="h-9 px-4 rounded-md bg-destructive text-white text-sm flex items-center gap-1" data-testid={`att-reject-${rec.id}`}><X className="size-4" /> Reject</button>
                            <button onClick={() => onDecide(rec.id, "approved")} className="h-9 px-4 rounded-md bg-emerald-600 text-white text-sm flex items-center gap-1" data-testid={`att-approve-${rec.id}`}><Check className="size-4" /> Approve</button>
                        </div>
                    ) : <span className="text-xs text-muted-foreground">{rec.payroll_locked || rec.payroll_run_id ? "Locked into payroll — read-only." : "No pending review decision."}</span>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function Attendance() {
    const [searchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState({});
    const [start, setStart] = useState(searchParams.get("start") || "");
    const [end, setEnd] = useState(searchParams.get("end") || "");
    const [suspiciousOnly, setSuspiciousOnly] = useState(false);
    const [detail, setDetail] = useState(null);

    const load = async () => {
        const params = {};
        if (start) params.start_date = start;
        if (end) params.end_date = end;
        const [att, emp] = await Promise.all([api.get("/timekeeping/attendance", { params }), api.get("/hris/employees", { params: { limit: 500 } })]);
        const map = {};
        (emp.data.items || []).forEach((e) => map[e.id] = `${e.last_name}, ${e.first_name}`);
        setEmployees(map);
        setItems(att.data.items || []);
    };
    useEffect(() => { load(); }, [start, end]);

    const filtered = suspiciousOnly ? items.filter((a) => (a.suspicious_flags || []).length) : items;

    const decide = async (id, decision) => {
        let reason = null;
        if (decision === "rejected") {
            reason = window.prompt("Rejection reason?");
            if (!reason) return;
        }
        try {
            await api.post(`/operations/approvals/attendance/${id}/decide`, { decision, reason });
            toast.success(`Marked ${decision}`);
            setDetail(null);
            load();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
        }
    };

    return (
        <div>
            <PageHeader
                title="Attendance Review"
                subtitle="HR/Admin view of all clock-ins with GPS, geofence, selfie, and suspicious-flag detection."
            />
            <Surface className="p-3 mb-3 flex items-center gap-2 flex-wrap">
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 px-3 rounded-md border border-border text-sm" data-testid="att-start" />
                <span className="text-muted-foreground text-xs">to</span>
                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 px-3 rounded-md border border-border text-sm" data-testid="att-end" />
                <label className="ml-auto text-xs flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={suspiciousOnly} onChange={(e) => setSuspiciousOnly(e.target.checked)} data-testid="att-suspicious-only" />
                    Suspicious only
                </label>
            </Surface>
            <TableWrap>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Employee</th>
                        <th>Time In</th>
                        <th>Time Out</th>
                        <th className="num">Hrs</th>
                        <th>Break / Home</th>
                        <th>GPS</th>
                        <th className="num">Acc.</th>
                        <th>Geofence</th>
                        <th>Selfie</th>
                        <th>Method</th>
                        <th>Flags</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((a) => (
                        <tr key={a.id} data-row-id={a.id} className="cursor-pointer hover:bg-secondary/50" onClick={() => setDetail(a)} data-testid={`att-${a.id}`}>
                            <td className="font-mono">{a.work_date}{a.is_holiday && <span title={`${a.holiday_name || "Holiday"} (${a.holiday_type})`} className="ml-1">🎌</span>}</td>
                            <td>{employees[a.employee_id] || a.employee_id?.slice(0, 8)}</td>
                            <td className="font-mono text-xs">{a.time_in ? formatDateTime(a.time_in) : "—"}</td>
                            <td className="font-mono text-xs">{a.time_out ? formatDateTime(a.time_out) : "—"}</td>
                            <td className="num">{(a.hours_worked || 0).toFixed(2)}</td>
                            <td className="text-xs">{[a.break_source === "recorded" ? `${a.break_minutes} min break` : null, a.work_from_home ? "from home" : null].filter(Boolean).join(", ") || "—"}</td>
                            <td className="font-mono text-[11px]">{a.gps_in?.lat ? `${a.gps_in.lat.toFixed(4)}, ${a.gps_in.lng.toFixed(4)}` : <span className="text-destructive">missing</span>}</td>
                            <td className="num text-xs">{a.accuracy_meters_in ? `±${Math.round(a.accuracy_meters_in)}m` : "—"}</td>
                            <td>{a.geofence_result ? <StatusBadge value={a.geofence_result} /> : "—"}</td>
                            <td>{a.selfie_in_path ? <Camera className="size-3 text-emerald-600 inline" /> : <span className="text-muted-foreground text-xs">—</span>}</td>
                            <td><StatusBadge value={a.source} /></td>
                            <td>
                                <div className="flex flex-wrap gap-1">
                                    {(a.suspicious_flags || []).map((f) => {
                                        const m = FLAG_META[f] || { icon: AlertTriangle, label: f, color: "text-muted-foreground" };
                                        const Icon = m.icon;
                                        return (
                                            <span key={f} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${m.color} bg-secondary`} data-testid={`flag-${f}`}>
                                                <Icon className="size-2.5" /> {m.label}
                                            </span>
                                        );
                                    })}
                                    {!(a.suspicious_flags || []).length && <span className="text-[10px] text-muted-foreground">—</span>}
                                </div>
                            </td>
                            <td>
                                {a.payroll_locked || a.payroll_run_id ? (
                                    <Badge variant="outline"><Lock className="size-3 mr-1" /> payroll-locked</Badge>
                                ) : a.review_status === "approved" ? (
                                    <Badge>approved</Badge>
                                ) : a.review_status === "rejected" ? (
                                    <Badge variant="destructive">rejected</Badge>
                                ) : a.approval_status === "pending" ? (
                                    <Badge variant="outline" className="border-amber-400 text-amber-700">🎌 holiday — needs approval</Badge>
                                ) : a.manual_override ? (
                                    <Badge variant="outline">pending review</Badge>
                                ) : (
                                    <span className="text-[10px] text-muted-foreground">auto</span>
                                )}
                            </td>
                            <td>
                                <button className={`h-7 px-2 text-xs rounded-md flex items-center gap-1 ${(a.manual_override || a.approval_status === "pending") && !a.payroll_locked && !a.payroll_run_id && !["approved", "rejected"].includes(a.review_status) ? "bg-primary text-white" : "border border-border"}`}
                                    onClick={(e) => { e.stopPropagation(); setDetail(a); }} data-testid={`att-review-${a.id}`}>
                                    <Eye className="size-3" /> {(a.manual_override || a.approval_status === "pending") && !a.payroll_locked && !a.payroll_run_id && !["approved", "rejected"].includes(a.review_status) ? "Review" : "View"}
                                </button>
                            </td>
                        </tr>
                    ))}
                    {!filtered.length && <tr><td colSpan={14}><Empty title="No attendance records" subtitle="Adjust the date range or filters." /></td></tr>}
                </tbody>
            </TableWrap>

            {detail && (
                <AttendanceDetail
                    rec={detail}
                    empName={employees[detail.employee_id] || detail.employee_id?.slice(0, 8)}
                    onClose={() => setDetail(null)}
                    onDecide={decide}
                />
            )}
        </div>
    );
}
